// -- Constants
const MM_WAITLIST_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const MM_WAITLIST_TABLET = window.matchMedia('(min-width: 750px)');
const MM_WAITLIST_DESKTOP = window.matchMedia('(min-width: 1440px)');

// -- Functions
function mmWaitlistScrollBehavior() {
  return MM_WAITLIST_REDUCED_MOTION.matches ? 'auto' : 'smooth';
}

function mmWaitlistTrackGap(track) {
  if (!track) return 0;
  const styles = window.getComputedStyle(track);
  return parseFloat(styles.columnGap || styles.gap) || 0;
}

// -- Custom element
class MmWaitlist extends HTMLElement {
  connectedCallback() {
    this._viewport = this.querySelector('[data-mm-waitlist-viewport]') || this.querySelector('.mm-waitlist__viewport');
    this._track = this.querySelector('[data-mm-waitlist-track]');
    this._slides = Array.from(this.querySelectorAll('[data-mm-waitlist-slide]'));
    this._pages = Array.from(this.querySelectorAll('[data-mm-waitlist-page]'));
    this._dotsHost = this.querySelector('[data-mm-waitlist-dots]');
    this._prev = this.querySelector('[data-mm-waitlist-prev]');
    this._next = this.querySelector('[data-mm-waitlist-next]');
    this._dots = [];
    this._pageSize = 1;
    this._currentPage = 0;
    this._pageLabel = this._dotsHost ? this._dotsHost.getAttribute('data-page-label') || '' : '';

    this._onScroll = this._onScroll.bind(this);
    this._onPrev = this._onPrev.bind(this);
    this._onNext = this._onNext.bind(this);
    this._onDot = this._onDot.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onBlockSelect = this._onBlockSelect.bind(this);
    this._onFocusIn = this._onFocusIn.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);

    if (this._track) {
      this._track.addEventListener('scroll', this._onScroll, { passive: true });
      this._track.addEventListener('focusin', this._onFocusIn);
    }
    if (this._viewport) {
      this._viewport.addEventListener('pointerdown', this._onPointerDown);
      this._viewport.addEventListener('pointerup', this._onPointerUp);
      this._viewport.addEventListener('pointercancel', this._onPointerUp);
    }
    if (this._prev) this._prev.addEventListener('click', this._onPrev);
    if (this._next) this._next.addEventListener('click', this._onNext);
    this.addEventListener('shopify:block:select', this._onBlockSelect);
    window.addEventListener('resize', this._onResize);

    this._resizeObserver = new ResizeObserver(this._onResize);
    if (this._viewport) {
      this._resizeObserver.observe(this._viewport);
    } else if (this._track) {
      this._resizeObserver.observe(this._track);
    }

    this._updateLayout();
  }

  disconnectedCallback() {
    if (this._track) {
      this._track.removeEventListener('scroll', this._onScroll);
      this._track.removeEventListener('focusin', this._onFocusIn);
    }
    if (this._viewport) {
      this._viewport.removeEventListener('pointerdown', this._onPointerDown);
      this._viewport.removeEventListener('pointerup', this._onPointerUp);
      this._viewport.removeEventListener('pointercancel', this._onPointerUp);
    }
    if (this._prev) this._prev.removeEventListener('click', this._onPrev);
    if (this._next) this._next.removeEventListener('click', this._onNext);
    this._dots.forEach((dot) => dot.removeEventListener('click', this._onDot));
    this.removeEventListener('shopify:block:select', this._onBlockSelect);
    window.removeEventListener('resize', this._onResize);
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  }

  _isMobile() {
    return !MM_WAITLIST_TABLET.matches;
  }

  _measurePageSize() {
    if (!this._slides.length) return 1;
    if (this._isMobile()) return 4;

    if (MM_WAITLIST_DESKTOP.matches) {
      return 4;
    }

    return 3;
  }

  _pageCount() {
    if (!this._slides.length) return 1;
    return Math.max(1, Math.ceil(this._slides.length / this._pageSize));
  }

  _updateLayout() {
    const nextSize = this._measurePageSize();
    if (nextSize !== this._pageSize) {
      // Keep the first visible product in view when the column count changes.
      this._currentPage = Math.floor(this._currentPage * this._pageSize / nextSize);
      this._pageSize = nextSize;
      this.style.setProperty('--mm-waitlist-page-size', String(nextSize));
    }

    const pageCount = this._pageCount();
    this._currentPage = Math.max(0, Math.min(this._currentPage, pageCount - 1));

    this._rebuildDots();
    this.classList.toggle('mm-waitlist--paged', pageCount > 1);

    if (this._isMobile()) {
      if (this._track) {
        this._track.style.transform = '';
      }
      this._applyPage(this._currentPage, false);
    } else {
      if (this._track) {
        this._track.scrollLeft = 0;
      }
      this._applyPage(this._currentPage, false);
    }
  }

  _applyPage(page, animate = true) {
    if (!this._track || !this._slides.length) return;

    this._currentPage = Math.max(0, Math.min(page, this._pageCount() - 1));

    if (this._isMobile()) {
      const targetSlide = this._pages[this._currentPage];
      if (targetSlide) {
        const paddingLeft = parseFloat(window.getComputedStyle(this._track).paddingLeft) || 0;
        const targetLeft = targetSlide.offsetLeft - paddingLeft;
        this._track.scrollTo({
          left: targetLeft,
          behavior: animate ? mmWaitlistScrollBehavior() : 'auto',
        });
      }
    } else {
      const startIndex = this._currentPage * this._pageSize;
      const targetSlide = this._slides[startIndex];
      let offset = 0;
      if (targetSlide) {
        offset = -(targetSlide.offsetLeft - this._slides[0].offsetLeft);
      } else {
        const gap = mmWaitlistTrackGap(this._track);
        const viewportWidth = this._track.clientWidth;
        offset = -this._currentPage * (viewportWidth + gap);
      }

      if (!animate) {
        this._track.style.transition = 'none';
      }
      this._track.style.transform = `translateX(${offset}px)`;
      this._slides.forEach((slide, index) => {
        slide.toggleAttribute('data-mm-waitlist-offpage', index < startIndex || index >= startIndex + this._pageSize);
      });
      if (!animate) {
        void this._track.offsetHeight;
        this._track.style.transition = '';
      }
    }

    this._syncControls();
  }

  _rebuildDots() {
    if (!this._dotsHost) return;

    const pageCount = this._pageCount();
    const showDots = pageCount > 1;

    this._dotsHost.hidden = !showDots;
    this._dotsHost.toggleAttribute('hidden', !showDots);

    if (!showDots) {
      this._dots.forEach((dot) => dot.removeEventListener('click', this._onDot));
      this._dotsHost.replaceChildren();
      this._dots = [];
      return;
    }

    if (this._dots.length === pageCount) return;

    this._dots.forEach((dot) => dot.removeEventListener('click', this._onDot));
    this._dotsHost.replaceChildren();
    this._dots = [];

    for (let i = 0; i < pageCount; i += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mm-waitlist__dot';
      button.setAttribute('data-mm-waitlist-dot', String(i));
      button.setAttribute('aria-label', `${this._pageLabel} ${i + 1}`.trim());
      button.addEventListener('click', this._onDot);
      this._dotsHost.appendChild(button);
      this._dots.push(button);
    }
  }

  _onScroll() {
    if (!this._isMobile() || !this._track || !this._slides.length) return;
    const scrollLeft = this._track.scrollLeft;
    let closestIndex = 0;
    let minDistance = Infinity;

    const paddingLeft = parseFloat(window.getComputedStyle(this._track).paddingLeft) || 0;
    this._pages.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - paddingLeft - scrollLeft);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    this._currentPage = closestIndex;
    this._setActiveDot(this._currentPage);
  }

  _onResize() {
    this._updateLayout();
  }

  _onPrev() {
    if (this._currentPage > 0) {
      this._applyPage(this._currentPage - 1);
    }
  }

  _onNext() {
    if (this._currentPage < this._pageCount() - 1) {
      this._applyPage(this._currentPage + 1);
    }
  }

  _onDot(event) {
    const index = Number(event.currentTarget.getAttribute('data-mm-waitlist-dot'));
    if (Number.isNaN(index)) return;
    this._applyPage(index);
  }

  _onBlockSelect(event) {
    const slide = event.target.closest('[data-mm-waitlist-slide]');
    if (!slide) return;
    const index = this._slides.indexOf(slide);
    if (index < 0) return;

    this._applyPage(Math.floor(index / this._pageSize));
  }

  _onFocusIn(event) {
    const slide = event.target.closest('[data-mm-waitlist-slide]');
    if (!slide) return;
    const index = this._slides.indexOf(slide);
    if (index < 0) return;
    const targetPage = Math.floor(index / this._pageSize);
    if (targetPage !== this._currentPage) {
      this._applyPage(targetPage);
    }
  }

  _onPointerDown(event) {
    if (this._isMobile()) return;
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    this._pointerStartX = event.clientX;
    this._pointerStartY = event.clientY;
    this._isPointerDragging = true;
  }

  _onPointerUp(event) {
    if (!this._isPointerDragging || this._isMobile()) return;
    this._isPointerDragging = false;
    const deltaX = event.clientX - this._pointerStartX;
    const deltaY = event.clientY - this._pointerStartY;

    if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX < 0) {
        this._onNext();
      } else {
        this._onPrev();
      }
    }
  }

  _syncControls() {
    this._setActiveDot(this._currentPage);
    this._syncArrows();
  }

  _setActiveDot(page) {
    this._dots.forEach((dot, i) => {
      const active = i === page;
      dot.classList.toggle('is-active', active);
      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  _syncArrows() {
    const pageCount = this._pageCount();
    const atStart = this._currentPage <= 0;
    const atEnd = this._currentPage >= pageCount - 1;

    this._toggleArrow(this._prev, atStart);
    this._toggleArrow(this._next, atEnd);
  }

  _toggleArrow(button, disabled) {
    if (!button) return;
    button.disabled = disabled;
    button.setAttribute('aria-disabled', disabled.toString());
  }
}

class MmWaitlistCard extends HTMLElement {
  connectedCallback() {
    this._image = this.querySelector('[data-mm-waitlist-image]');
    this._source = this.querySelector('[data-mm-waitlist-desktop-source]');
    this._variantInput = this.querySelector('[name="id"]');
    this._cta = this.querySelector('[data-mm-waitlist-cta]');
    this._swatches = Array.from(this.querySelectorAll('[data-mm-waitlist-swatch]'));
    this._more = this.querySelector('[data-mm-waitlist-more]');
    this._swatchWrap = this.querySelector('[data-mm-waitlist-swatches]');
    this._clickable = this.getAttribute('data-clickable') === 'true';
    this._action = this.getAttribute('data-action');

    if (this._image) {
      this._defaultSrc = this._image.getAttribute('data-default-src') || this._image.getAttribute('src') || '';
      this._defaultSrcset = this._image.getAttribute('data-default-srcset') || this._image.getAttribute('srcset') || '';
    }
    if (this._source) {
      this._defaultSource = this._source.getAttribute('data-default-srcset') || this._source.getAttribute('srcset') || '';
    }

    this._onSwatch = this._onSwatch.bind(this);
    this._onMore = this._onMore.bind(this);

    this._swatches.forEach((swatch) => swatch.addEventListener('click', this._onSwatch));
    if (this._more) this._more.addEventListener('click', this._onMore);
  }

  disconnectedCallback() {
    this._swatches.forEach((swatch) => swatch.removeEventListener('click', this._onSwatch));
    if (this._more) this._more.removeEventListener('click', this._onMore);
  }

  _onMore() {
    if (!this._swatchWrap) return;
    this._swatchWrap.classList.add('is-expanded');
    if (this._more) this._more.hidden = true;
  }

  _onSwatch(event) {
    const swatch = event.currentTarget;
    this._selectSwatch(swatch);
    this._applyVariant(swatch);
  }

  _selectSwatch(swatch) {
    this._swatches.forEach((item) => {
      const selected = item === swatch;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-pressed', selected.toString());
    });
  }

  _applyVariant(swatch) {
    const image = swatch.getAttribute('data-image');
    const srcset = swatch.getAttribute('data-srcset');
    const variantId = swatch.getAttribute('data-variant-id');
    const available = swatch.getAttribute('data-available') === 'true';

    if (this._image) {
      if (image) {
        this._image.src = image;
        if (srcset) this._image.srcset = srcset;
        if (this._source) this._source.srcset = srcset;
      } else if (this._defaultSrc) {
        this._image.src = this._defaultSrc;
        this._image.srcset = this._defaultSrcset;
        if (this._source) this._source.srcset = this._defaultSource;
      }
    }

    if (this._variantInput && variantId) {
      this._variantInput.value = variantId;
    }

    if (this._action === 'add_to_cart' && this._clickable && this._cta) {
      this._cta.disabled = !available;
      this._cta.classList.toggle('is-disabled', !available);
      this._cta.setAttribute('aria-disabled', (!available).toString());
    }
  }
}

if (!customElements.get('mm-waitlist')) {
  customElements.define('mm-waitlist', MmWaitlist);
}

if (!customElements.get('mm-waitlist-card')) {
  customElements.define('mm-waitlist-card', MmWaitlistCard);
}
